import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../i18n/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  LogOut,
  User,
  MapPin,
  TreePine,
  Clock,
  DollarSign,
  Camera,
  Upload,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Target,
  Hammer,
  Eye,
  FileImage,
  X,
  Navigation,
  ArrowRight,
  Link2,
  Copy,
  ExternalLink
} from 'lucide-react';

const API = `${import.meta.env.REACT_APP_BACKEND_URL}/api`;

// Status configurations
const statusConfig = {
  available: { color: 'bg-slate-500', label: 'Available', icon: Target },
  funded: { color: 'bg-green-500', label: 'Ready to Claim', icon: DollarSign },
  claimed: { color: 'bg-blue-500', label: 'In Progress', icon: Hammer },
  submitted: { color: 'bg-amber-500', label: 'Awaiting Review', icon: Eye },
  verified: { color: 'bg-emerald-500', label: 'Verified', icon: CheckCircle2 },
  rejected: { color: 'bg-red-500', label: 'Rejected', icon: AlertCircle }
};

// Mission Card for available missions
function MissionCard({ zone, onClaim, claiming }) {
  const status = statusConfig[zone.status] || statusConfig.available;
  const StatusIcon = status.icon;
  
  return (
    <Card className="overflow-hidden hover:shadow-md transition-shadow">
      <div className={`${status.color} px-4 py-2 flex items-center justify-between`}>
        <div className="flex items-center gap-2 text-white">
          <StatusIcon className="w-4 h-4" />
          <span className="text-sm font-medium">{status.label}</span>
        </div>
        <Badge variant="outline" className="bg-white/20 text-white border-white/30 text-xs">
          {zone.method === 'manual' ? 'Manual' : 'Mechanical'}
        </Badge>
      </div>
      
      <CardContent className="p-4">
        <h3 className="font-bold text-foreground text-lg mb-2">{zone.name}</h3>
        <p className="text-muted-foreground text-sm mb-4 line-clamp-2">{zone.description}</p>
        
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="flex items-center gap-2 text-sm">
            <TreePine className="w-4 h-4 text-green-600" />
            <span>{zone.hectares || '?'} hectares</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Clock className="w-4 h-4 text-blue-600" />
            <span>{zone.estimated_hours || '?'} hours</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <MapPin className="w-4 h-4 text-amber-600" />
            <span className="truncate">{zone.location || 'Epuyén'}</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <DollarSign className="w-4 h-4 text-emerald-600" />
            <span className="font-bold">${zone.bounty_target_amount?.toLocaleString()}</span>
          </div>
        </div>
        
        {zone.status === 'funded' && (
          <Button 
            onClick={() => onClaim(zone.zone_id)}
            disabled={claiming}
            className="w-full"
            data-testid={`claim-mission-${zone.zone_id}`}
          >
            {claiming ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Claiming...</>
            ) : (
              <><Hammer className="w-4 h-4 mr-2" /> Claim This Mission</>
            )}
          </Button>
        )}
        
        {zone.status === 'available' && (
          <div className="text-center text-sm text-muted-foreground">
            <Progress value={zone.funding_progress || 0} className="h-2 mb-2" />
            <span>{zone.funding_progress || 0}% funded - waiting for donors</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Claimed Mission Card with proof submission
function ClaimedMissionCard({ claim, onSubmitProof, submitting, workerToken }) {
  const [showProofForm, setShowProofForm] = useState(false);
  const [beforePhotos, setBeforePhotos] = useState([]);
  const [afterPhotos, setAfterPhotos] = useState([]);
  const [notes, setNotes] = useState('');
  const [gpsLocation, setGpsLocation] = useState({ lat: null, lng: null });
  const [gettingLocation, setGettingLocation] = useState(false);
  const [uploadLink, setUploadLink] = useState(null);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  
  const status = statusConfig[claim.status] || statusConfig.claimed;
  const StatusIcon = status.icon;
  
  const handleGetLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return;
    }
    
    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setGpsLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        });
        setGettingLocation(false);
        toast.success('Location captured!');
      },
      (error) => {
        console.error('Geolocation error:', error);
        toast.error('Could not get your location. Please enable location services.');
        setGettingLocation(false);
      },
      { enableHighAccuracy: true }
    );
  };
  
  const handleBeforePhotos = (e) => {
    const files = Array.from(e.target.files);
    setBeforePhotos(prev => [...prev, ...files.slice(0, 5 - prev.length)]);
  };
  
  const handleAfterPhotos = (e) => {
    const files = Array.from(e.target.files);
    setAfterPhotos(prev => [...prev, ...files.slice(0, 5 - prev.length)]);
  };
  
  const handleSubmit = async () => {
    if (beforePhotos.length === 0) {
      toast.error('Please add at least one "before" photo');
      return;
    }
    if (afterPhotos.length === 0) {
      toast.error('Please add at least one "after" photo');
      return;
    }
    
    // Create FormData for file upload
    const formData = new FormData();
    beforePhotos.forEach((photo, idx) => {
      formData.append('before_photos', photo);
    });
    afterPhotos.forEach((photo, idx) => {
      formData.append('after_photos', photo);
    });
    formData.append('notes', notes);
    if (gpsLocation.lat && gpsLocation.lng) {
      formData.append('gps_lat', gpsLocation.lat);
      formData.append('gps_lng', gpsLocation.lng);
    }
    
    await onSubmitProof(claim.claim_id, formData);
    setShowProofForm(false);
  };
  
  const handleGenerateLink = async () => {
    setGeneratingLink(true);
    try {
      const response = await fetch(`${API}/marketplace/claims/${claim.claim_id}/generate-proof-link`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${workerToken}` }
      });
      const data = await response.json();
      if (response.ok) {
        setUploadLink(data.upload_link);
        toast.success('Upload link generated!');
      } else {
        toast.error(data.detail || 'Failed to generate link');
      }
    } catch {
      toast.error('Network error');
    } finally {
      setGeneratingLink(false);
    }
  };

  const handleCopyLink = () => {
    if (uploadLink) {
      navigator.clipboard.writeText(uploadLink).then(() => {
        setLinkCopied(true);
        toast.success('Link copied to clipboard!');
        setTimeout(() => setLinkCopied(false), 3000);
      });
    }
  };
  
  return (
    <Card className="overflow-hidden">
      <div className={`${status.color} px-4 py-2 flex items-center justify-between`}>
        <div className="flex items-center gap-2 text-white">
          <StatusIcon className="w-4 h-4" />
          <span className="text-sm font-medium">{status.label}</span>
        </div>
        <span className="text-white/80 text-xs">
          Claimed {new Date(claim.claimed_at).toLocaleDateString()}
        </span>
      </div>
      
      <CardContent className="p-4">
        <h3 className="font-bold text-foreground text-lg mb-2">{claim.zone_name || 'Mission Zone'}</h3>
        
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="flex items-center gap-2 text-sm">
            <TreePine className="w-4 h-4 text-green-600" />
            <span>{claim.hectares || '?'} hectares</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <DollarSign className="w-4 h-4 text-emerald-600" />
            <span className="font-bold">${claim.bounty_amount?.toLocaleString() || '?'}</span>
          </div>
        </div>
        
        {/* Instructions */}
        {claim.instructions && (
          <div className="bg-blue-50 dark:bg-blue-950/30 rounded-lg p-3 mb-4">
            <h4 className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-1">Instructions:</h4>
            <p className="text-sm text-blue-600 dark:text-blue-400">{claim.instructions}</p>
          </div>
        )}
        
        {/* Action Buttons */}
        {claim.status === 'claimed' && !showProofForm && (
          <div className="space-y-3">
            <Button 
              onClick={handleGenerateLink}
              disabled={generatingLink}
              variant="default"
              className="w-full"
              data-testid={`get-upload-link-${claim.claim_id}`}
            >
              {generatingLink ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating Link...</>
              ) : (
                <><Link2 className="w-4 h-4 mr-2" /> Get Upload Link</>
              )}
            </Button>

            {uploadLink && (
              <div className="bg-emerald-50 dark:bg-emerald-950/30 rounded-lg p-3 space-y-2">
                <p className="text-xs font-medium text-emerald-700 dark:text-emerald-300 flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" /> Upload link ready
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCopyLink}
                    className="flex-1"
                    data-testid={`copy-link-${claim.claim_id}`}
                  >
                    {linkCopied ? (
                      <><CheckCircle2 className="w-3 h-3 mr-1" /> Copied</>
                    ) : (
                      <><Copy className="w-3 h-3 mr-1" /> Copy Link</>
                    )}
                  </Button>
                  <a href={uploadLink} target="_blank" rel="noopener noreferrer" className="flex-1">
                    <Button variant="outline" size="sm" className="w-full" data-testid={`open-link-${claim.claim_id}`}>
                      <ExternalLink className="w-3 h-3 mr-1" /> Open
                    </Button>
                  </a>
                </div>
              </div>
            )}

            <div className="relative flex items-center">
              <div className="flex-grow border-t border-border" />
              <span className="mx-3 text-xs text-muted-foreground">or upload here</span>
              <div className="flex-grow border-t border-border" />
            </div>

            <Button 
              onClick={() => setShowProofForm(true)}
              variant="outline"
              className="w-full"
              data-testid={`submit-proof-${claim.claim_id}`}
            >
              <Camera className="w-4 h-4 mr-2" /> Submit Proof Directly
            </Button>
          </div>
        )}
        
        {claim.status === 'submitted' && (
          <div className="text-center py-4">
            <Eye className="w-8 h-8 mx-auto text-amber-500 mb-2" />
            <p className="text-sm text-muted-foreground">
              Your proof is being reviewed by administrators.
              You will be notified when verification is complete.
            </p>
          </div>
        )}
        
        {claim.status === 'verified' && (
          <div className="text-center py-4 bg-emerald-50 dark:bg-emerald-950/30 rounded-lg">
            <CheckCircle2 className="w-8 h-8 mx-auto text-emerald-500 mb-2" />
            <p className="text-sm font-medium text-emerald-700 dark:text-emerald-300">
              Mission Verified! Payment processing.
            </p>
          </div>
        )}
        
        {claim.status === 'rejected' && (
          <div className="text-center py-4 bg-red-50 dark:bg-red-950/30 rounded-lg">
            <AlertCircle className="w-8 h-8 mx-auto text-red-500 mb-2" />
            <p className="text-sm font-medium text-red-700 dark:text-red-300 mb-2">
              Proof rejected. {claim.rejection_reason || 'Please submit new proof.'}
            </p>
            <Button variant="outline" size="sm" onClick={() => setShowProofForm(true)}>
              Resubmit Proof
            </Button>
          </div>
        )}
        
        {/* Proof Submission Form */}
        <AnimatePresence>
          {showProofForm && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-4 pt-4 border-t border-border"
            >
              <h4 className="font-medium text-foreground mb-4">Submit Proof of Work</h4>
              
              {/* Before Photos */}
              <div className="mb-4">
                <label className="text-sm font-medium text-foreground mb-2 block">
                  Before Photos (up to 5)
                </label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {beforePhotos.map((file, idx) => (
                    <div key={idx} className="relative w-20 h-20 rounded-lg overflow-hidden bg-muted">
                      <img 
                        src={URL.createObjectURL(file)} 
                        alt={`Before ${idx + 1}`}
                        className="w-full h-full object-cover"
                      />
                      <button
                        onClick={() => setBeforePhotos(prev => prev.filter((_, i) => i !== idx))}
                        className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  {beforePhotos.length < 5 && (
                    <label className="w-20 h-20 border-2 border-dashed border-border rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-primary transition-colors">
                      <Camera className="w-6 h-6 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">Add</span>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        onChange={handleBeforePhotos}
                        data-testid="before-photos-input"
                      />
                    </label>
                  )}
                </div>
              </div>
              
              {/* After Photos */}
              <div className="mb-4">
                <label className="text-sm font-medium text-foreground mb-2 block">
                  After Photos (up to 5)
                </label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {afterPhotos.map((file, idx) => (
                    <div key={idx} className="relative w-20 h-20 rounded-lg overflow-hidden bg-muted">
                      <img 
                        src={URL.createObjectURL(file)} 
                        alt={`After ${idx + 1}`}
                        className="w-full h-full object-cover"
                      />
                      <button
                        onClick={() => setAfterPhotos(prev => prev.filter((_, i) => i !== idx))}
                        className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  {afterPhotos.length < 5 && (
                    <label className="w-20 h-20 border-2 border-dashed border-border rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-primary transition-colors">
                      <Camera className="w-6 h-6 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">Add</span>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        onChange={handleAfterPhotos}
                        data-testid="after-photos-input"
                      />
                    </label>
                  )}
                </div>
              </div>
              
              {/* GPS Location */}
              <div className="mb-4">
                <label className="text-sm font-medium text-foreground mb-2 block">
                  GPS Location
                </label>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleGetLocation}
                    disabled={gettingLocation}
                    data-testid="get-location-button"
                  >
                    {gettingLocation ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Getting location...</>
                    ) : (
                      <><Navigation className="w-4 h-4 mr-2" /> Capture Location</>
                    )}
                  </Button>
                  {gpsLocation.lat && (
                    <span className="text-sm text-green-600">
                      <CheckCircle2 className="w-4 h-4 inline mr-1" />
                      {gpsLocation.lat.toFixed(5)}, {gpsLocation.lng.toFixed(5)}
                    </span>
                  )}
                </div>
              </div>
              
              {/* Notes */}
              <div className="mb-4">
                <label className="text-sm font-medium text-foreground mb-2 block">
                  Notes (optional)
                </label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add any notes about the work completed..."
                  rows={3}
                  data-testid="proof-notes-input"
                />
              </div>
              
              {/* Submit Buttons */}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowProofForm(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={submitting || beforePhotos.length === 0 || afterPhotos.length === 0}
                  className="flex-1"
                  data-testid="submit-proof-button"
                >
                  {submitting ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</>
                  ) : (
                    <><Upload className="w-4 h-4 mr-2" /> Submit Proof</>
                  )}
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </CardContent>
    </Card>
  );
}

const WorkerDashboardPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [worker, setWorker] = useState(null);
  const [availableMissions, setAvailableMissions] = useState([]);
  const [myClaims, setMyClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('available');
  
  // Check authentication
  useEffect(() => {
    const token = localStorage.getItem('worker_token');
    const workerId = localStorage.getItem('worker_id');
    const workerName = localStorage.getItem('worker_name');
    const workerEmail = localStorage.getItem('worker_email');
    
    if (!token || !workerId) {
      navigate('/worker/login');
      return;
    }
    
    setWorker({ id: workerId, name: workerName, email: workerEmail, token });
  }, [navigate]);
  
  // Fetch data
  const fetchData = useCallback(async () => {
    if (!worker) return;
    
    setLoading(true);
    try {
      // Fetch available missions (funded zones)
      const zonesRes = await fetch(`${API}/marketplace/zones?status=funded`);
      if (zonesRes.ok) {
        const zonesData = await zonesRes.json();
        setAvailableMissions(zonesData.zones || []);
      }
      
      // Fetch my claims
      const claimsRes = await fetch(`${API}/marketplace/workers/${worker.id}/claims`, {
        headers: { 'Authorization': `Bearer ${worker.token}` }
      });
      if (claimsRes.ok) {
        const claimsData = await claimsRes.json();
        setMyClaims(claimsData.claims || []);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load missions');
    } finally {
      setLoading(false);
    }
  }, [worker]);
  
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  // Claim a mission
  const handleClaim = async (zoneId) => {
    if (!worker) return;
    
    setClaiming(true);
    try {
      const response = await fetch(`${API}/marketplace/zones/${zoneId}/claim`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${worker.token}`
        },
        body: JSON.stringify({ worker_id: worker.id })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success('Mission claimed successfully!');
        fetchData();
        setActiveTab('my-missions');
      } else {
        toast.error(data.detail || 'Failed to claim mission');
      }
    } catch (error) {
      console.error('Claim error:', error);
      toast.error('Network error. Please try again.');
    } finally {
      setClaiming(false);
    }
  };
  
  // Submit proof
  const handleSubmitProof = async (claimId, formData) => {
    if (!worker) return;
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API}/marketplace/claims/${claimId}/submit-proof`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${worker.token}`
        },
        body: formData
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success('Proof submitted successfully!');
        fetchData();
      } else {
        toast.error(data.detail || 'Failed to submit proof');
      }
    } catch (error) {
      console.error('Submit proof error:', error);
      toast.error('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };
  
  // Logout
  const handleLogout = () => {
    localStorage.removeItem('worker_token');
    localStorage.removeItem('worker_id');
    localStorage.removeItem('worker_name');
    localStorage.removeItem('worker_email');
    navigate('/worker/login');
  };
  
  if (!worker) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-green-950/5">
      {/* Header */}
      <header className="bg-card border-b border-border sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-2">
              <TreePine className="w-8 h-8 text-primary" />
              <span className="font-bold text-foreground text-lg hidden sm:inline">Epuyén Renace</span>
            </Link>
            <span className="text-muted-foreground hidden sm:inline">|</span>
            <span className="text-sm font-medium text-muted-foreground hidden sm:inline">Worker Portal</span>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-sm">
              <User className="w-4 h-4 text-muted-foreground" />
              <span className="text-foreground hidden sm:inline">{worker.name || worker.email}</span>
            </div>
            <Button variant="outline" size="sm" onClick={handleLogout} data-testid="logout-button">
              <LogOut className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="p-4 text-center">
              <Target className="w-6 h-6 mx-auto text-green-600 mb-2" />
              <p className="text-2xl font-bold text-foreground">{availableMissions.length}</p>
              <p className="text-xs text-muted-foreground">Available</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Hammer className="w-6 h-6 mx-auto text-blue-600 mb-2" />
              <p className="text-2xl font-bold text-foreground">
                {myClaims.filter(c => c.status === 'claimed').length}
              </p>
              <p className="text-xs text-muted-foreground">In Progress</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Eye className="w-6 h-6 mx-auto text-amber-600 mb-2" />
              <p className="text-2xl font-bold text-foreground">
                {myClaims.filter(c => c.status === 'submitted').length}
              </p>
              <p className="text-xs text-muted-foreground">Pending Review</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <CheckCircle2 className="w-6 h-6 mx-auto text-emerald-600 mb-2" />
              <p className="text-2xl font-bold text-foreground">
                {myClaims.filter(c => c.status === 'verified').length}
              </p>
              <p className="text-xs text-muted-foreground">Completed</p>
            </CardContent>
          </Card>
        </div>
        
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full justify-start mb-6">
            <TabsTrigger value="available" data-testid="tab-available">
              Available Missions ({availableMissions.length})
            </TabsTrigger>
            <TabsTrigger value="my-missions" data-testid="tab-my-missions">
              My Missions ({myClaims.length})
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="available">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : availableMissions.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Target className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium text-foreground mb-2">No Missions Available</h3>
                  <p className="text-muted-foreground">
                    Check back later for new funded missions ready to claim.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {availableMissions.map(zone => (
                  <MissionCard 
                    key={zone.zone_id} 
                    zone={zone} 
                    onClaim={handleClaim}
                    claiming={claiming}
                  />
                ))}
              </div>
            )}
          </TabsContent>
          
          <TabsContent value="my-missions">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : myClaims.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Hammer className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium text-foreground mb-2">No Claimed Missions</h3>
                  <p className="text-muted-foreground mb-4">
                    You haven&apos;t claimed any missions yet.
                  </p>
                  <Button variant="outline" onClick={() => setActiveTab('available')}>
                    Browse Available Missions <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {myClaims.map(claim => (
                  <ClaimedMissionCard 
                    key={claim.claim_id} 
                    claim={claim} 
                    onSubmitProof={handleSubmitProof}
                    submitting={submitting}
                    workerToken={worker.token}
                  />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default WorkerDashboardPage;
